import { Box, Button, Grid, Typography } from '@mui/material';
import api from 'api';
import BodyTemplate from 'components/_layout/navigation/BodyTemplate';
import { GenericPaginator } from 'components/generic-table';
import AddIcon from '@mui/icons-material/Add';
import FindPatientsForm from 'components/find-patients-form';
import PatientList from 'components/patient-list';
import { useContextRedirection, useCurrentUser, useNotification, useQuery } from 'hooks';
import { Patient, PatientsListRequest, PatientsListResponse } from 'models/patients.model';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGINATION_SIZE } from 'utils/Constants';
import RoutePaths from 'utils/RoutePaths';
import { formatCreatedPatient } from 'utils/func/Person.func';
import { allPropsAreTruthy } from 'utils';
import { PrescriptionRequestMaker } from 'utils/func/Prescription.func';

export const CREATE_BY_CNS_LENGTH = 13;

const paginatorData: GenericPaginator = {
  total: 0,
  size: DEFAULT_PAGINATION_SIZE,
  page: 1,
};

const Patients: FC = () => {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const queryParams = useQuery();
  const navigate = useContextRedirection();
  const currentUser = useCurrentUser();

  const [isDataFromMPI, setIsDataFromMPI] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCreatePatientButton, setShowCreatePatientButton] = useState<boolean>(false);

  const formData: PatientsListRequest = {
    first_name: undefined,
    last_name: undefined,
    cns: undefined,
    gender: undefined,
    birth_name: undefined,
    birth_date: undefined,
  };

  const [form, setForm] = useState(formData);
  const handleFormData = (data: PatientsListRequest) => {
    setForm(data);
    const resetedPaginator = { ...paginator, page: 1 };
    setPaginator(resetedPaginator);
    requestPatientList(resetedPaginator, data);
  };

  const [paginator, setPaginator] = useState(paginatorData);
  const patientListData: Patient[] | undefined = undefined;
  const [patientList, setPatientList] = useState<Patient[] | undefined>(patientListData);

  const handlePaginatorData = (data: GenericPaginator) => {
    setPaginator(data);
    requestPatientList(data, form);
  };
  const requestPatientListMPI = useCallback(
    (data: PatientsListRequest) => {
      setIsLoading(true);

      api.patients
        .getPatientsMPI(data)
        .then((response: any) => {
          setIsDataFromMPI(true);
          setPatientList(response);
          setPaginator({ ...paginator, total: response.length });
          setIsLoading(false);
        })
        .catch((error) => {
          if (error?.code === 400) {
            notification(t('landing_page.login.token.notification.error.incorrectToken'), 'error');
            return;
          }
          setPatientList([]);
          setPaginator(paginatorData);
          setIsLoading(false);
        });
    },
    [notification, paginator, t],
  );

  useEffect(() => {
    queryParams.get('search') &&
      api.patients
        .getPatientMultipleSearch(queryParams.toString())
        .then((response) => {
          setPatientList(response.results);
          setPaginator({
            total: response.total,
            size: response.size,
            page: response.page,
          });
        })
        .catch(() => notification(t('patients.notification.error'), 'error'));
  }, [notification, queryParams, t]);

  const requestPatientList = useCallback(
    (paginator: GenericPaginator, form: PatientsListRequest) => {
      setIsLoading(true);
      api.patients
        .getPatients(form, paginator.size, paginator.page)
        .then((response: PatientsListResponse) => {
          if (response.total === 0) {
            requestPatientListMPI(form);
          } else {
            setIsDataFromMPI(false);
            setPatientList(response.results);
            setPaginator({
              total: response.total,
              size: response.size,
              page: response.page,
            });
            setIsLoading(false);
          }
        })
        .catch(() => {
          setPatientList([]);
          notification(t('prescriptions.notification.error'), 'error');
          setIsLoading(false);
        })
        .finally(() => setShowCreatePatientButton(true));
    },
    [currentUser?.patient_creation_enabled, notification, requestPatientListMPI, t],
  );

  const requestCreatePatient = useCallback(
    (patient: any) => {
      const { birth_name, ...formatedPatient } = formatCreatedPatient(patient);
      if (allPropsAreTruthy(formatedPatient)) {
        api.patients
          .createPatient({ ...formatedPatient, home: patient.addresses?.home })
          .then((response) => {
            PrescriptionRequestMaker({ patientId: response.id, navigation: navigate });
          })
          .catch((e) => {
            const errorMessages = ['There is more than one patient with that social security number.', 'There is more than one patient with those data.'];
            if (errorMessages.includes(e.response?.data?.message)) {
              notification(`${t('patients.create.errors.already_exist')}`, 'error');
            }
          });
      }
    },
    [navigate, notification, t],
  );

  return (
    <BodyTemplate title={t('bodyTemplate.doctor.patients')}>
      <Grid container columnSpacing={2} rowSpacing={3}>
        <Grid item xs={12} sm={4}>
          <FindPatientsForm handleFormAction={handleFormData} />
        </Grid>
        <Grid item xs={12} sm={8}>
          <PatientList
            patientDetailAction={{ to: 'DOCTOR_PATIENTS_DETAILS' }}
            patientList={patientList}
            paginatorData={paginator}
            handlePaginatorAction={handlePaginatorData}
            requestCreatePatient={requestCreatePatient}
            isDataFromMPI={isDataFromMPI}
            isLoading={isLoading}
          />

          {showCreatePatientButton && !!currentUser?.patient_creation_enabled && form.cns?.length === CREATE_BY_CNS_LENGTH && (
            <Box display='flex' alignItems='center' justifyContent='flex-end' mt={3}>
              <Typography variant='h2' fontSize='14px' mr={2}>
                {t('patients.create.description')}
              </Typography>
              <Button variant='contained' startIcon={<AddIcon />} onClick={() => navigate(RoutePaths['DOCTOR_CREATE_PATIENTS'])}>
                {t('patients.create.button_title')}
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
    </BodyTemplate>
  );
};

export default Patients;
